/**
 * Spec 0050 — resource-limit plans / entitlements (saas-auth SASATH-0062).
 *
 * SUPER_ADMIN-gated CRUD on saas-auth's `/api/saas-admin/**` surface, in three
 * groups:
 *   - per-product limit **definitions** (levers): list/create/update/remove
 *     (`key` immutable after create — update touches label/default only);
 *   - per-product **plans** (tiers): list/create/update/remove, plus
 *     `setPlanLimits` upserting a plan's `{definition → value}` set;
 *   - per-tenant **assignment**: `assignPlan` / `unassignPlan`.
 * saas-admin stores nothing and enforces nothing — it just calls saas-auth.
 *
 * ENVELOPE: built FIRMLY to the SINGLE documented SASATH-0062 contract (no
 * tolerant/dual-shape parsing). Every response is the standard `ApiResponse<T>`
 * envelope (like every other `api/admin/*` module), so this uses the strict
 * `request<ApiResponse<T>>()` + `unwrap()` pattern of internalKeys/ghlIntegration.
 * The shipped SASATH-0062 delivery covers only the schema/entities (U1); the
 * saas-admin CRUD DTOs are not yet shipped, so the shapes here are ASSUMED from
 * the documented contract — see `types.ts` CONTRACT STATUS. Lever values are
 * strings interpreted per `valueType` (gap #2 → strings).
 */
import { request, unwrap } from '../client';
import type {
  ApiResponse,
  AssignTenantPlanRequest,
  CreateLimitDefinitionRequest,
  CreatePlanRequest,
  LimitDefinition,
  Plan,
  PlanLimitValue,
  SetPlanLimitsRequest,
  TenantPlanAssignment,
  UpdateLimitDefinitionRequest,
  UpdatePlanRequest,
} from '../types';

const definitionsBase = (productId: number) =>
  `/api/saas-admin/products/${productId}/limit-definitions`;
const plansBase = (productId: number) => `/api/saas-admin/products/${productId}/plans`;
const tenantPlanPath = (tenantId: number) => `/api/saas-admin/tenants/${tenantId}/plan`;

export const resourceLimitsApi = {
  definitions: {
    async list(productId: number): Promise<LimitDefinition[]> {
      const res = await request<ApiResponse<LimitDefinition[]>>('GET', definitionsBase(productId));
      return unwrap(res);
    },

    async create(
      productId: number,
      req: CreateLimitDefinitionRequest,
    ): Promise<LimitDefinition> {
      const res = await request<ApiResponse<LimitDefinition>>(
        'POST',
        definitionsBase(productId),
        { json: req },
      );
      return unwrap(res);
    },

    async update(
      productId: number,
      definitionId: number,
      req: UpdateLimitDefinitionRequest,
    ): Promise<LimitDefinition> {
      const res = await request<ApiResponse<LimitDefinition>>(
        'PUT',
        `${definitionsBase(productId)}/${definitionId}`,
        { json: req },
      );
      return unwrap(res);
    },

    async remove(productId: number, definitionId: number): Promise<void> {
      await request<void>('DELETE', `${definitionsBase(productId)}/${definitionId}`);
    },
  },

  plans: {
    /**
     * List the product's plans. Each `Plan` EMBEDS its set `limits`
     * (SASATH-0064) — the lever metadata + value inline — so the plans table and
     * the per-plan limits editor render/seed from the list response with no
     * separate plan-values read or definitions join. See the `Plan` type note
     * (revised hand-off; the `limits` embedding is not yet shipped in saas-auth).
     */
    async list(productId: number): Promise<Plan[]> {
      const res = await request<ApiResponse<Plan[]>>('GET', plansBase(productId));
      return unwrap(res);
    },

    async create(productId: number, req: CreatePlanRequest): Promise<Plan> {
      const res = await request<ApiResponse<Plan>>('POST', plansBase(productId), { json: req });
      return unwrap(res);
    },

    async update(productId: number, planId: number, req: UpdatePlanRequest): Promise<Plan> {
      const res = await request<ApiResponse<Plan>>(
        'PUT',
        `${plansBase(productId)}/${planId}`,
        { json: req },
      );
      return unwrap(res);
    },

    async remove(productId: number, planId: number): Promise<void> {
      await request<void>('DELETE', `${plansBase(productId)}/${planId}`);
    },
  },

  tenantPlan: {
    /**
     * Read the tenant's current plan assignment — `{ planId, planName }`, or
     * `null` when no plan is assigned (the tenant then falls back to each
     * definition's default). The dedicated read that REPLACES spec-0050's
     * `TenantDto.planId` assumption. NOTE: not yet in the shipped saas-auth
     * controller — built firmly to the revised SASATH-0062 hand-off (see the
     * `TenantPlanAssignment` type note + the 0051 delivery).
     */
    async get(tenantId: number): Promise<TenantPlanAssignment | null> {
      const res = await request<ApiResponse<TenantPlanAssignment | null>>(
        'GET',
        tenantPlanPath(tenantId),
      );
      return unwrap(res);
    },
  },

  /**
   * Upsert a plan's limit values — the full `{definition → value}` set. The
   * backend validates each value against its definition's `valueType` and
   * rejects (400) cross-product definitions. Returns the resulting value set —
   * the dedicated `PUT …/plans/{planId}/limits` is left UNCHANGED by SASATH-0064,
   * so it returns the lean per-value set (`{definitionId, value}` — the wire also
   * carries `key`), NOT the richer embedded `Plan.limits` array. The table/editor
   * re-read the embedded `limits` from a fresh `plans.list` after a save.
   */
  async setPlanLimits(
    productId: number,
    planId: number,
    values: PlanLimitValue[],
  ): Promise<PlanLimitValue[]> {
    const body: SetPlanLimitsRequest = { values };
    const res = await request<ApiResponse<PlanLimitValue[]>>(
      'PUT',
      `${plansBase(productId)}/${planId}/limits`,
      { json: body },
    );
    return unwrap(res);
  },

  /**
   * Assign a plan to a tenant. The plan must belong to the tenant's product,
   * else the backend 400s.
   */
  async assignPlan(tenantId: number, planId: number): Promise<void> {
    const body: AssignTenantPlanRequest = { planId };
    await request<unknown>('PUT', tenantPlanPath(tenantId), { json: body });
  },

  /** Unassign the tenant's plan (it falls back to definition defaults). */
  async unassignPlan(tenantId: number): Promise<void> {
    await request<unknown>('DELETE', tenantPlanPath(tenantId));
  },
};
